import { BaseCampaignPatchItemDTO } from '@retail/campaign/types';
import { useCampaignTFunction } from '@retail/campaign/i18n';
import { CampaignTenantType, useUpdateCampaignItem } from '@retail/campaign/data-access';
import { useToast } from '@retail/hooks';
import { useCallback, useMemo } from 'react';
import { useInvalidateCampaignQueries } from './useInvalidateCampaignQueries';

interface Props {
  campaignId: string;
  type: CampaignTenantType;
}

export const useUpdateCampaignItemsActions = <T extends BaseCampaignPatchItemDTO>({
  campaignId,
  type,
}: Props) => {
  const toast = useToast();
  const t = useCampaignTFunction();

  const { mutateAsync: mutateUpdateCampaignItem, isPending: isLoadingUpdateCampaignItem } =
    useUpdateCampaignItem<T>({ campaignId, type });

  const invalidateCampaignQueries = useInvalidateCampaignQueries({ campaignId });

  const handleUpdateItem = useCallback(
    async (id: string, item: T) => {
      try {
        await mutateUpdateCampaignItem({
          urlVariables: id,
          body: item,
        });
        invalidateCampaignQueries();

        toast.success(t('campaign.messages.itemUpdated'));
      } catch (error) {
        console.error(error);
        toast.error(t('campaign.error.genericError'));
      }
    },
    [invalidateCampaignQueries, mutateUpdateCampaignItem, t, toast]
  );

  return useMemo(
    () => ({
      isLoadingUpdateCampaignItem,
      handleUpdateItem,
    }),
    [isLoadingUpdateCampaignItem, handleUpdateItem]
  );
};
